// services/jobService.ts - COMPLETE VERSION with ALL original methods + MySQL integration
import axios from "axios";
import { Job } from "../models/Job.interface";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

// Import the JobDataAdapter
import { JobDataAdapter } from "./JobDataAdapter";

// All original interfaces
export interface JobSearchParams {
  page?: number;
  limit?: number;
  status?: string[];
  categories?: string[];
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  radius?: number;
  centerLat?: number;
  centerLng?: number;
}

export interface JobSearchResponse {
  jobs: Job[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore?: boolean;
  center?: {
    latitude: number;
    longitude: number;
    city?: string;
    state?: string;
  };
  radius?: number;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  query?: string;
  location?: string;
}

export interface LocationSearchParams {
  query: string;
  type: "city" | "zipcode" | "address";
  radius?: number;
}

export interface JobLocation {
  city: string;
  state: string;
  count: number;
  slug?: string;
  stateSlug?: string;
}

export interface JobCategory {
  category: string;
  count: number;
}

export interface JobStats {
  total: number;
  byStatus: Record<string, number>;
  byCategory: Record<string, number>;
  recentCount: number;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  zipCode?: string;
  formattedAddress: string;
  jobCount?: number;
}

class JobService {
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_SERVER || "https://www.bidbro.com/api";
  }

  // ========== NEW MYSQL-FIRST METHODS WITH ADAPTER ==========

/**
   * Enhanced fetchJob method with better error handling and job_properties support
   */
  async fetchJob(jobId: string): Promise<Job | null> {
    try {
      console.log(`🔄 Fetching job: ${jobId}`);

      // Validate jobId
      if (!jobId || jobId.trim() === "") {
        throw new Error("Job ID cannot be empty");
      }

      const response = await axios.get(`${this.baseURL}/jobs/${jobId}`);

      if (response.status === 200 && response.data) {
        console.log("✅ Got job from MySQL API");
        console.log("📋 Raw job data from API:", response.data);
        
        // The API now returns propertyDetails if available
        if (response.data.propertyDetails) {
          console.log("✅ Job includes property details:", response.data.propertyDetails);
        } else {
          console.log("ℹ️  Job has no additional property details data");
        }

        // Check for job_value field
        if (response.data.jobValue !== undefined) {
          console.log("✅ Job includes job_value:", response.data.jobValue);
        }
        
        return JobDataAdapter.mysqlToJob(response.data);
      } else {
        throw new Error(`MySQL fetch failed: ${response.status}`);
      }
    } catch (mysqlError) {
      console.log(
        "❌ MySQL fetch failed, trying Firebase fallback",
        mysqlError
      );
      return await this.fetchJobFromFirebase(jobId);
    }
  }

  /**
   * Fetch job by SEO URL and Firebase ID - MySQL first with Firebase fallback
   * Now includes job_properties data and job_value
   */
  async fetchJobByUrl(seoUrl: string, firebaseId: string): Promise<Job | null> {
    try {
      console.log("🔄 Fetching job by URL from MySQL API...");
      console.log("📋 Request params:", { seoUrl, firebaseId });

      // FIXED: Use the correct endpoint structure that exists in the backend
      // The backend route is /public/:seo_url/:job_id (not firebase_id)
      const response = await axios.get(
        `${this.baseURL}/jobs/public/${encodeURIComponent(
          seoUrl
        )}/${firebaseId}`
      );

      console.log("📡 MySQL API Response Status:", response.status);
      console.log("📡 MySQL API Raw Response Data:", response.data);

      if (response.status === 200 && response.data) {
        console.log("✅ Got job from MySQL public route");

        // Check if job_properties data is included
        if (response.data.propertyDetails) {
          console.log("✅ Public job includes property details data:", response.data.propertyDetails);
        }

        // Check for job_value field
        if (response.data.jobValue !== undefined) {
          console.log("✅ Public job includes job_value:", response.data.jobValue);
        }

        // Now that the backend is fixed, it should return proper MySQL field names
        // We can use the JobDataAdapter to convert it properly
        const jobData = JobDataAdapter.mysqlToJob(response.data);

        console.log("✅ Converted job data from MySQL:", jobData);
        console.log("📋 Job title:", jobData.jobTitle);
        console.log("📋 Job ID:", jobData.id);
        console.log("📋 Job value:", jobData.jobValue);
        console.log("📋 Property address:", jobData.propertyAddress);
        console.log("📋 Job property details:", jobData.propertyDetails);
        console.log("📋 All job data fields present:", {
          hasTitle: !!jobData.jobTitle,
          hasId: !!jobData.id,
          hasDescription: !!jobData.jobDetail,
          hasAddress: !!jobData.propertyAddress,
          hasCategories: (jobData.jobCategories || []).length > 0,
          hasAttachments: (jobData.jobAttachments || []).length > 0,
          hasPropertyDetails: !!jobData.propertyDetails,
          hasJobValue: jobData.jobValue !== undefined
        });

        return jobData;
      } else {
        throw new Error(`MySQL public fetch failed: ${response.status}`);
      }
    } catch (mysqlError) {
      console.log(
        "❌ MySQL public fetch failed, trying Firebase fallback",
        mysqlError
      );
      return await this.fetchJobFromFirebase(firebaseId);
    }
  }

  /**
   * Update job - converts Job format to MySQL format, with Firebase fallback
   * Now preserves job_properties data during updates
   */
  async updateJobWithAdapter(job: Job): Promise<Job> {
    try {
      console.log("🔄 Updating job via MySQL API...");
      console.log("📋 Job to update includes properties:", !!job.propertyDetails);
      
      // Convert Job format to MySQL format using adapter
      const mysqlData = JobDataAdapter.jobToMySQL(job);

      const response = await axios.put(
        `${this.baseURL}/jobs/${job.id}`,
        mysqlData
      );

      if (response.status === 200 && response.data) {
        console.log("✅ Job updated via MySQL");
        
        // Check if updated job includes property data
        if (response.data.propertyDetails) {
          console.log("✅ Updated job includes property details data");
        }
        
        // Convert back to Job format for return
        return JobDataAdapter.mysqlToJob(response.data);
      } else {
        throw new Error(`Update failed: ${response.status}`);
      }
    } catch (mysqlError) {
      console.error(
        "❌ MySQL update failed, trying Firebase fallback",
        mysqlError
      );
      return await this.updateJobInFirebase(job);
    }
  }

  /**
   * Send chat message - MySQL first with Firebase fallback
   */
  async sendChatMessage(
    jobId: string,
    messageData: {
      message: string;
      role: "User" | "Contractor" | "BidBro";
      sender_name: string;
      sender_id?: string;
    }
  ): Promise<boolean> {
    try {
      console.log("🔄 Sending chat message via MySQL API...");
      const response = await axios.post(
        `${this.baseURL}/jobs/${jobId}/messages`,
        {
          message_text: messageData.message,
          role: messageData.role,
          sender_name: messageData.sender_name,
          sender_id: messageData.sender_id,
          timestamp: new Date().toISOString(),
        }
      );

      if (response.status === 200 || response.status === 201) {
        console.log("✅ Chat message sent via MySQL");
        return true;
      } else {
        throw new Error(`Message send failed: ${response.status}`);
      }
    } catch (mysqlError) {
      console.error(
        "❌ MySQL message send failed, trying Firebase fallback",
        mysqlError
      );
      return await this.sendChatMessageToFirebase(jobId, messageData);
    }
  }

  /**
   * Associate job with user - enhanced with better error handling
   */
  async associateJobWithUser(jobId: string, userId: string): Promise<boolean> {
    try {
      console.log(`🔄 Associating job ${jobId} with user ${userId}`);

      if (!jobId || !userId) {
        throw new Error("Both jobId and userId are required");
      }

      const response = await axios.put(`${this.baseURL}/jobs/${jobId}`, {
        userId: userId,
      });

      if (response.status === 200) {
        console.log("✅ Successfully associated job with user via MySQL");
        return true;
      } else {
        throw new Error(`Association failed: ${response.status}`);
      }
    } catch (mysqlError) {
      console.log("❌ MySQL association failed, trying Firebase", mysqlError);

      try {
        // Firebase fallback
        const firestore = firebase.firestore();
        await firestore.collection("jobs").doc(jobId).update({
          userId: userId,
          dtLastModified: firebase.firestore.FieldValue.serverTimestamp(),
        });

        console.log("✅ Successfully associated job with user via Firebase");
        return true;
      } catch (firebaseError) {
        console.error("❌ Firebase association also failed", firebaseError);
        return false;
      }
    }
  }

  // ========== FIREBASE FALLBACK METHODS ==========

  /**
   * Firebase fallback for fetching jobs
   */
  private async fetchJobFromFirebase(jobId: string): Promise<Job | null> {
    try {
      console.log("🔄 Fetching job from Firebase...");
      const firestore = firebase.firestore();
      const doc = await firestore.collection("jobs").doc(jobId).get();

      if (doc.exists) {
        const data = { id: doc.id, ...doc.data() } as Job;
        console.log("✅ Got job from Firebase");
        return data; // Firebase data is already in Job format
      }
      return null;
    } catch (error) {
      console.error("❌ Firebase fallback failed:", error);
      return null;
    }
  }

  /**
   * Firebase fallback for updating jobs
   */
  private async updateJobInFirebase(job: Job): Promise<Job> {
    try {
      console.log("🔄 Updating job in Firebase...");
      const firestore = firebase.firestore();
      const jobRef = firestore.collection("jobs").doc(job.id);
      await jobRef.update(job);
      console.log("✅ Job updated in Firebase");
      return job;
    } catch (error) {
      console.error("❌ Firebase job update failed:", error);
      throw error;
    }
  }

  /**
   * Firebase fallback for sending chat messages
   */
  private async sendChatMessageToFirebase(
    jobId: string,
    messageData: any
  ): Promise<boolean> {
    try {
      console.log("🔄 Sending chat message to Firebase...");
      const firestore = firebase.firestore();
      const jobRef = firestore.collection("jobs").doc(jobId);
      const jobDoc = await jobRef.get();

      if (jobDoc.exists) {
        const currentMessages = jobDoc.data()?.chatMessages || [];
        await jobRef.update({
          chatMessages: [
            ...currentMessages,
            {
              ...messageData,
              timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            },
          ],
        });
        console.log("✅ Chat message sent to Firebase");
        return true;
      }
      return false;
    } catch (error) {
      console.error("❌ Firebase chat message failed:", error);
      return false;
    }
  }

  /**
   * Firebase fallback for associating job with user
   */
  private async associateJobWithUserInFirebase(
    jobId: string,
    userId: string
  ): Promise<boolean> {
    try {
      console.log("🔄 Associating job with user in Firebase...");
      const firestore = firebase.firestore();
      const jobRef = firestore.collection("jobs").doc(jobId);

      await jobRef.update({
        userId: userId,
        dtUserAssociated: firebase.firestore.FieldValue.serverTimestamp(),
      });

      console.log("✅ Job associated with user in Firebase");
      return true;
    } catch (error) {
      console.error("❌ Firebase association failed:", error);
      return false;
    }
  }

  // ========== ALL ORIGINAL METHODS (PRESERVED) ==========

  /**
   * Updated getJobsByCity method with MySQL first approach and proper attachments
   */
  async getJobsByCity(
    city: string,
    state: string,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      console.log(`🔄 Fetching jobs for ${city}, ${state} from MySQL API...`);

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: (params.page || 1).toString(),
        limit: (params.limit || 20).toString(),
      });

      if (params.status && params.status.length > 0) {
        queryParams.append("status", params.status.join(","));
      }

      if (params.categories && params.categories.length > 0) {
        queryParams.append("categories", params.categories.join(","));
      }

      if (params.bounds) {
        queryParams.append("north", params.bounds.north.toString());
        queryParams.append("south", params.bounds.south.toString());
        queryParams.append("east", params.bounds.east.toString());
        queryParams.append("west", params.bounds.west.toString());
      }

      const response = await axios.get(
        `${this.baseURL}/jobs/city/${encodeURIComponent(
          city
        )}/${encodeURIComponent(state)}?${queryParams}`
      );

      console.log("📡 MySQL API Response Status:", response.status);
      console.log("📡 MySQL API Response:", response.data);

      if (response.status === 200 && response.data) {
        console.log("✅ Got jobs from MySQL city endpoint");
        console.log(`📊 Found ${response.data.jobs?.length || 0} jobs`);

        /*
        // Log attachment info for debugging
        if (response.data.jobs) {
          response.data.jobs.forEach((job: any, index: number) => {
            console.log(
              `📎 Job ${index + 1} (${job.job_title}) has ${
                (job.attachments || []).length
              } attachments`
            );
          });
        }
        */
        // FIXED: Use the new adapter method for job arrays
        const convertedJobs = JobDataAdapter.mysqlJobsArrayToJobs(
          response.data
        );

        console.log(
          `✅ Converted ${convertedJobs.length} jobs with attachments`
        );

        /*
        // Log converted jobs attachment info
        convertedJobs.forEach((job, index) => {
          console.log(
            `📎 Converted Job ${index + 1} (${job.jobTitle}) has ${
              (job.jobAttachments || []).length
            } attachments`
          );
        });
        */
        return {
          jobs: convertedJobs,
          total: response.data.total || 0,
          page: response.data.page || 1,
          limit: response.data.limit || 20,
          totalPages: response.data.totalPages || 0,
          center: response.data.center,
        };
      } else {
        throw new Error(`MySQL city fetch failed: ${response.status}`);
      }
    } catch (mysqlError) {
      console.log(
        "❌ MySQL city fetch failed, falling back to basic response",
        mysqlError
      );

      if (axios.isAxiosError(mysqlError)) {
        console.error("❌ Axios error:", {
          status: mysqlError.response?.status,
          statusText: mysqlError.response?.statusText,
          data: mysqlError.response?.data,
          message: mysqlError.message,
        });
      }

      // Return empty result since we don't have a Firebase city method
      console.log(
        "⚠️ No Firebase fallback available for city search, returning empty result"
      );
      return {
        jobs: [],
        total: 0,
        page: params.page || 1,
        limit: params.limit || 20,
        totalPages: 0,
        // Don't provide center if we don't have proper coordinates
        center: undefined,
      };
    }
  }

  /**
   * Get jobs by zip code (FIXED: Updated with correct API path)
   */
  async getJobsByZipCode(
    zipCode: string,
    radius: number = 10,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/zipcode/${zipCode}`,
        {
          params: {
            radius,
            page: params.page || 1,
            limit: params.limit || 20,
            status: params.status?.join(","),
            categories: params.categories?.join(","),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching jobs by zip code:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  /**
   * Get jobs by coordinates with radius (FIXED: Updated with correct API path)
   */
  async getJobsByLocation(
    latitude: number,
    longitude: number,
    radius: number = 10,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/location`,
        {
          params: {
            lat: latitude,
            lng: longitude,
            radius,
            page: params.page || 1,
            limit: params.limit || 20,
            status: params.status?.join(","),
            categories: params.categories?.join(","),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching jobs by location:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  /**
   * Get jobs within map bounds (FIXED: Updated with correct API path)
   */
  async getJobsByBounds(
    bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    },
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/bounds`,
        {
          params: {
            north: bounds.north,
            south: bounds.south,
            east: bounds.east,
            west: bounds.west,
            page: params.page || 1,
            limit: params.limit || 50,
            status: params.status?.join(","),
            categories: params.categories?.join(","),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching jobs by bounds:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  /**
   * Search jobs by text query (FIXED: Updated with correct API path)
   */
  async searchJobs(
    query: string,
    location?: string,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/search`,
        {
          params: {
            q: query,
            location,
            page: params.page || 1,
            limit: params.limit || 20,
            status: params.status?.join(","),
            categories: params.categories?.join(","),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error searching jobs:", error);
      throw new Error("Failed to search jobs");
    }
  }

  /**
   * Get job by ID (FIXED: Updated with correct API path)
   */
  async getJobById(id: string): Promise<Job> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job by ID:", error);
      throw new Error("Failed to fetch job");
    }
  }

  /**
   * Get job categories with counts (FIXED: Updated with correct API path)
   */
  async getJobCategories(location?: string): Promise<JobCategory[]> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/categories`,
        {
          params: { location },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job categories:", error);
      throw new Error("Failed to fetch job categories");
    }
  }

  /**
   * Get popular job locations (FIXED: Updated with correct API path)
   */
  async getPopularJobLocations(limit: number = 10): Promise<JobLocation[]> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/locations/popular`,
        {
          params: { limit },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching popular job locations:", error);
      throw new Error("Failed to fetch popular job locations");
    }
  }

  /**
   * Geocode location for job search (FIXED: Updated with correct API path)
   */
  async geocodeLocation(params: LocationSearchParams): Promise<GeocodeResult> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/geocode`,
        {
          params,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error geocoding location:", error);
      throw new Error("Failed to geocode location");
    }
  }

  /**
   * Get job statistics for dashboard (FIXED: Updated with correct API path)
   */
  async getJobStats(location?: string): Promise<JobStats> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/stats`,
        {
          params: { location },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job stats:", error);
      throw new Error("Failed to fetch job statistics");
    }
  }

  /**
   * Get all job locations for route generation (FIXED: Updated with correct API path)
   */
  async getAllJobLocations(): Promise<JobLocation[]> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/locations/all`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all job locations:", error);
      throw new Error("Failed to fetch all job locations");
    }
  }

  /**
   * Get jobs by state (FIXED: Updated with correct API path)
   */
  async getJobsByState(
    state: string,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/search`,
        {
          params: {
            location: state,
            page: params.page || 1,
            limit: params.limit || 20,
            status: params.status?.join(","),
            categories: params.categories?.join(","),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching jobs by state:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  /**
   * Get jobs by category across all locations (FIXED: Updated with correct API path)
   */
  async getJobsByCategory(
    category: string,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/search`,
        {
          params: {
            page: params.page || 1,
            limit: params.limit || 20,
            status: params.status?.join(","),
            categories: [category].join(","),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching jobs by category:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  /**
   * Get jobs by category and location (enhanced with caching)
   */
  async getJobsByCategoryAndLocation(
    category: string,
    city: string,
    state: string,
    params: JobSearchParams = {}
  ): Promise<JobSearchResponse> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/city/${city}/${state}`,
        {
          params: {
            page: params.page || 1,
            limit: params.limit || 20,
            status: params.status?.join(","),
            categories: [category].join(","),
            ...(params.bounds && {
              north: params.bounds.north,
              south: params.bounds.south,
              east: params.bounds.east,
              west: params.bounds.west,
            }),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching jobs by category and location:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  /**
   * Get public job by SEO URL and Job ID (updated endpoint)
   */
  async getPublicJob(seoUrl: string, jobId: string): Promise<Job | null> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/public/${seoUrl}/${jobId}`
      );

      return response.data || null;
    } catch (error) {
      console.error("Error fetching public job:", error);
      return null;
    }
  }

  /**
   * Get popular job locations
   */
  async getPopularLocations(limit: number = 50): Promise<
    Array<{
      city: string;
      state: string;
      jobCount: number;
    }>
  > {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/locations/popular`,
        {
          params: { limit },
        }
      );

      return response.data.locations || [];
    } catch (error) {
      console.error("Error fetching popular locations:", error);
      throw new Error("Failed to fetch popular locations");
    }
  }

  /**
   * Create initial job (FIXED: Updated with correct API path)
   */
  async createInitialJob(jobData: {
    jobId: string;
    ownerName: string;
    ownerEmail: string;
    ownerPhone?: string;
    contactPersonRole?: string;
    propertyAddress: any;
    userId?: string;
    jobStatus?: string;
  }): Promise<{ success: boolean; jobId: string }> {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/jobs/initial`,
        jobData
      );
      return response.data;
    } catch (error) {
      console.error("Error creating initial job:", error);
      throw new Error("Failed to create initial job");
    }
  }

  /**
   * Update existing job (EXISTING METHOD - kept for backward compatibility)
   */
  async updateJob(
    jobId: string,
    jobData: any
  ): Promise<{ success: boolean; jobId: string }> {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER}/jobs/${jobId}`,
        jobData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating job:", error);
      throw new Error("Failed to update job");
    }
  }

  /**
   * Clear job cache (FIXED: Updated with correct API path)
   */
  async clearCache(
    pattern?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_SERVER}/jobs/cache`,
        {
          params: { pattern },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error clearing cache:", error);
      throw new Error("Failed to clear cache");
    }
  }

  /**
   * Get health status of job service (FIXED: Updated with correct API path)
   */
  async getHealthStatus(): Promise<{
    success: boolean;
    service: string;
    timestamp: string;
    cache: {
      keys: number;
      hits: number;
      misses: number;
      hitRate: number;
    };
  }> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/jobs/health`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching health status:", error);
      throw new Error("Failed to fetch health status");
    }
  }

  // ========== HELPER METHODS ==========

  /**
   * Helper method to generate SEO-friendly URLs
   */
  generateJobUrl(job: Job): string {
    if (!job.seoUrl || !job.id) {
      return `/job-detail/${job.id}`;
    }

    return `/job/${job.seoUrl}/${job.id}`;
  }

  /**
   * Helper method to generate SEO-friendly job URLs
   */
  generateJobSeoUrl(jobTitle: string, city?: string, state?: string): string {
    let seoUrl = jobTitle
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens

    if (city && state) {
      const citySlug = city.toLowerCase().replace(/[^a-z0-9]/g, "-");
      const stateSlug = state.toLowerCase().replace(/[^a-z0-9]/g, "-");
      seoUrl += `-${citySlug}-${stateSlug}`;
    }

    return seoUrl;
  }

  /**
   * Helper method to format job location for display
   */
  formatJobLocation(job: Job): string {
    const { propertyAddress } = job;
    if (!propertyAddress) return "Location not specified";

    const parts = [];
    if (propertyAddress.city) parts.push(propertyAddress.city);
    if (propertyAddress.state) parts.push(propertyAddress.state);
    if (propertyAddress.zipCode) parts.push(propertyAddress.zipCode);

    return parts.join(", ");
  }

  /**
   * Helper method to get job status display information
   */
  getJobStatusInfo(status: string): {
    label: string;
    color: string;
    description: string;
  } {
    const statusMap: Record<
      string,
      { label: string; color: string; description: string }
    > = {
      Draft: {
        label: "Draft",
        color: "secondary",
        description: "Job is being created and not yet published",
      },
      Published: {
        label: "Published",
        color: "info",
        description: "Job is live and accepting bids",
      },
      Hiring: {
        label: "Hiring",
        color: "primary",
        description: "Contractors have been notified and are bidding",
      },
      "Pending Inspection": {
        label: "Pending Inspection",
        color: "warning",
        description: "Waiting for property inspection",
      },
      "Under Contract": {
        label: "Under Contract",
        color: "success",
        description: "Contract has been signed",
      },
      "Job In Progress": {
        label: "In Progress",
        color: "primary",
        description: "Work is currently being performed",
      },
      Complete: {
        label: "Complete",
        color: "success",
        description: "Job has been completed successfully",
      },
      Cancelled: {
        label: "Cancelled",
        color: "danger",
        description: "Job has been cancelled",
      },
    };

    return (
      statusMap[status] || {
        label: status,
        color: "secondary",
        description: "Unknown status",
      }
    );
  }
}

// Export singleton instance
export const jobService = new JobService();
export const jobAPI = jobService; // Add this export for backward compatibility
export default jobService;
